import { useMemo } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { Asset, createAsset, parseSmpte, SmpteController, UpNextConfig } from '@/organisms/AngelPlayer'
import { EngagementDetail } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { GetEpisodeResponse } from '@/services/ProjectsService'
import {
  ContentEpisode,
  Episode,
  NextContentEpisodeSpecialOrMovie,
  WatchableAvailabilityContent,
} from '@/services/ProjectsService/types'
import { GetEpisodeByGuidQuery } from '@/types/codegen-federation'
import { buildLinkToEpisode } from '@/utils/EpisodeUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

export interface UseEpisodeAssetConfig {
  momentsEnabled?: boolean
  defaultEngagementDetail?: Maybe<EngagementDetail>
}

export const usePureEpisodeAsset = (data?: GetEpisodeByGuidQuery): { asset?: Asset } => {
  const { query } = useSafeRouter()

  return useMemo(() => {
    const episode = data?.episode
    if (!episode) return { asset: undefined }

    const smpte = parseSmpte(getItemOrFirstEntry(query.smpte))
    const startTime = query?.position ? parseInt(query.position as string) : 0
    const position = smpte ?? startTime

    const asset = createAsset({
      guid: episode.guid as string,
      projectSlug: episode.projectSlug as string,
      slug: episode.slug as string,
      name: episode?.name as string,
      url: episode.source?.url as string,
      title: episode.subtitle as string,
      subtitle: episode.subtitle as string,
      description: episode.description as string,
      position,
      episodeNumber: episode.episodeNumber as number,
      releaseDate: episode.releaseDate,
      seasonId: episode.seasonId as string,
      duration: episode.source?.duration as number,
      videoType: 'episode',
      credits: episode.source?.credits as number,
    })

    return {
      asset,
    }
  }, [data?.episode, query])
}

/**
 * Validates a position against the credits timestamp. If the position is beyond
 * the credits, returns 0 (assumes user is rewatching). Otherwise returns the position.
 */
const validatePositionAgainstCredits = (
  position: number | undefined,
  credits: number | undefined,
): number | undefined => {
  // If no position provided, return undefined to allow fallback
  if (position === undefined || position === null) return undefined

  // If no credits marker, return the position as-is to avoid issues with bad metadata
  if (!credits) return position

  // If position is beyond credits, user is probably rewatching - start at beginning
  if (position > credits) {
    return 0
  }

  return position
}

const getPositionBasedOnWatchPositionAndCredits = (
  episode: GetEpisodeResponse['episode'],
  contentEpisode?: ContentEpisode,
) => {
  const watchPosition = contentEpisode?.watchPosition?.position ?? episode?.watchPosition?.position
  const credits = contentEpisode?.credits ?? episode?.source?.credits

  // if the user hasn't watched before, start at the beginning
  if (!watchPosition) return 0

  return validatePositionAgainstCredits(watchPosition, credits) ?? 0
}

const createUpNextMetadata = (
  nextContent: NextContentEpisodeSpecialOrMovie,
  project: GetEpisodeResponse['project'],
  credits: number,
  duration: number,
  navigateToEpisode: (episode: {
    projectSlug: string
    guid: string
    seasonNumber: number | null
    episodeNumber: number | null
    subtitle: string | null
    position: number
  }) => void,
  introStartTime?: number | null,
  introEndTime?: number | null,
): UpNextConfig | undefined => {
  if (!nextContent?.id || !project?.slug) {
    return undefined
  }

  return {
    goToNext: () => {
      const episode = {
        projectSlug:
          'project' in nextContent && 'slug' in nextContent.project && nextContent?.project?.slug
            ? nextContent.project.slug
            : project.slug,
        guid: nextContent.id,
        seasonNumber:
          'season' in nextContent && nextContent?.season?.seasonNumber ? nextContent.season.seasonNumber : null,
        episodeNumber: 'episodeNumber' in nextContent && nextContent?.episodeNumber ? nextContent.episodeNumber : null,
        subtitle: 'subtitle' in nextContent && nextContent?.subtitle ? nextContent.subtitle : null,
        position: 0,
      }
      navigateToEpisode(episode)
    },
    showTime: Math.max(0, Math.min(credits ? credits + 8 : Number.POSITIVE_INFINITY, duration - 15)),
    introStartTime,
    introEndTime,
  }
}

export const useEpisodeAsset = (
  episodeResponse?: GetEpisodeResponse,
  config?: UseEpisodeAssetConfig,
  controller?: SmpteController,
  contentEpisode?: ContentEpisode,
  nextContent?: NextContentEpisodeSpecialOrMovie | null,
  watchableContent?: WatchableAvailabilityContent,
): { asset?: Asset } => {
  const { push, query } = useSafeRouter()
  const { episode, project } = episodeResponse || {}

  const asset = useMemo(() => {
    if (!episode || !project) return

    const credits = contentEpisode?.credits ?? episode.source?.credits ?? 0
    const duration = contentEpisode?.duration ?? episode?.source?.duration ?? 0
    const introStartTime = contentEpisode?.introStartTime
    const introEndTime = contentEpisode?.introEndTime

    const upNextMetadata = nextContent
      ? createUpNextMetadata(
          nextContent,
          project,
          credits,
          duration,
          (ep) => push(buildLinkToEpisode(ep, { slug: project.slug, position: ep.position })),
          introStartTime,
          introEndTime,
        )
      : undefined

    const { episodeSourceUrl, description, releaseDate, vmapUrl, slug, name, guid } = resolveEpisodeFields({
      episode,
      contentEpisode,
      watchableContent,
    })
    const smpte = parseSmpte(getItemOrFirstEntry(query.smpte))
    const startTime = query.position ? parseInt(query.position as string) : undefined
    const controllerTime = controller?.getCurrentTime()

    // Validate query param position against credits if provided
    const validatedStartTime = validatePositionAgainstCredits(startTime, credits)

    const position =
      smpte ??
      validatedStartTime ??
      getPositionBasedOnWatchPositionAndCredits(episode, contentEpisode) ??
      controllerTime ??
      config?.defaultEngagementDetail?.startTime

    return createAsset({
      guid,
      publisherName: project.publisherName,
      projectSlug: project.slug,
      projectName: project.name,
      slug,
      name,
      url: episodeSourceUrl,
      title: episode.subtitle,
      subtitle: episode.subtitle,
      description,
      position,
      upNext: upNextMetadata,
      episodeNumber: episode.episodeNumber,
      releaseDate,
      seasonId: episode.seasonId,
      duration,
      videoType: 'episode',
      credits,
      vmapUrl,
    })
  }, [
    episode,
    project,
    contentEpisode,
    nextContent,
    watchableContent,
    query.smpte,
    query.position,
    controller,
    config?.defaultEngagementDetail?.startTime,
    push,
  ])

  return { asset }
}

export interface ResolveEpisodeFieldsArgs {
  episode: Episode
  contentEpisode?: ContentEpisode
  watchableContent?: WatchableAvailabilityContent
}

export interface ResolvedEpisodeFields {
  episodeSourceUrl: string
  description: string
  releaseDate?: string
  vmapUrl?: string
  slug: string
  name: string
  guid: string
}

export function resolveEpisodeFields({
  episode,
  contentEpisode,
  watchableContent,
}: ResolveEpisodeFieldsArgs): ResolvedEpisodeFields {
  const episodeSourceUrl = contentEpisode?.url ?? episode.source?.url ?? ''
  const description = contentEpisode?.description ?? episode.description ?? ''
  const releaseDate = contentEpisode?.originalReleaseDate ?? episode.releaseDate
  const vmapUrl =
    watchableContent && 'vmapUrl' in watchableContent
      ? watchableContent?.vmapUrl
      : episode.vmapUrl ?? contentEpisode?.vmapUrl
  const slug = contentEpisode?.slug ?? episode.slug ?? ''
  const name = contentEpisode?.name ?? episode.name ?? ''
  const guid = contentEpisode?.id ?? episode.guid ?? ''

  return {
    episodeSourceUrl,
    description,
    releaseDate,
    vmapUrl,
    slug,
    name,
    guid,
  }
}
